import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  SxProps,
  TextField,
  Theme,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { Autocomplete } from '~/components/Autocomplete';
import { AutocompleteOption } from '~/components/Autocomplete';
import { ADMIN_ROUTES, ROUTES } from '~/constants/routes';
import { useNotification } from '~/hooks/useNotification';
import { useUpdateEffect } from '~/hooks/useUpdateEffect';
import {
  useCreateUserMutation,
  useEditUserMutation,
  useGetUserPositionsQuery,
  useGetUserRolesQuery,
  useLazyGetUserQuery,
} from '~/services/rtkQuery/admin';
import { useAppDispatch } from '~/store/hooks';
import { setTokens } from '~/store/slices/user';
import { CreateUserFormValues } from '~/types/formValues';
import { formatUserPosition, formatUserRole } from '~/utils/formats';
import { createUserFormSchema, editUserFormSchema } from '~/utils/formSchemas';
import { handleUserFormikErrors } from '~/utils/helpers';
import { transformUserValidationErrors } from '~/utils/transforms';

import styles from './styles.module.scss';

const FIELD_SX: SxProps<Theme> = { mb: '23px' };

export const CreateUserPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { handleApiError, showNotification } = useNotification();
  const params = useParams<{ id: string }>();
  const userId = params.id ? +params.id : null;
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const passwordRef = useRef<HTMLDivElement>(null);
  const confirmPasswordRef = useRef<HTMLDivElement>(null);
  const getUserPositionsQuery = useGetUserPositionsQuery();
  const getUserRolesQuery = useGetUserRolesQuery();
  const [lazyGetUserQuery, { isFetching: isLoadingLazyGetUserQuery }] = useLazyGetUserQuery();
  const [createUserMutation, { isLoading: isLoadingCreateUserMutation }] = useCreateUserMutation();
  const [editUserMutation, { isLoading: isLoadingEditUserMutation }] = useEditUserMutation();

  const formik = useFormik<CreateUserFormValues>({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      position: null,
      role: null,
      password: '',
      confirmPassword: '',
    },
    validationSchema: userId ? editUserFormSchema : createUserFormSchema,
    onSubmit: async values => {
      try {
        if (userId) {
          const response = await editUserMutation({
            id: userId,
            first_name: values.firstName.trim(),
            last_name: values.lastName.trim(),
            email: values.email.trim(),
            position: values.position?.value as string,
            role: values.role?.value as string,
            password: values.password.trim(),
          }).unwrap();

          showNotification({
            type: 'success',
            title: response.title,
            message: response.message,
          });

          if (response.result?.access_token && response.result?.refresh_token) {
            dispatch(
              setTokens({
                access: response.result.access_token,
                refresh: response.result.refresh_token,
              }),
            );
          }
        } else {
          const response = await createUserMutation({
            first_name: values.firstName.trim(),
            last_name: values.lastName.trim(),
            email: values.email.trim(),
            position: values.position?.value as string,
            role: values.role?.value as string,
            password: values.password.trim(),
          }).unwrap();

          showNotification({
            type: 'success',
            title: response.title,
            message: response.message,
          });
        }

        navigate(ADMIN_ROUTES.USERS);
      } catch (e) {
        const errors = transformUserValidationErrors(e);

        if (errors) {
          handleUserFormikErrors(errors, showNotification);

          formik.setErrors(errors);
        } else {
          handleApiError(e);
        }
      }
    },
  });

  useEffect(() => {
    (async () => {
      try {
        if (userId) {
          const data = await lazyGetUserQuery({ id: userId }).unwrap();

          formik.resetForm({
            values: {
              firstName: data.result.data.first_name,
              lastName: data.result.data.last_name,
              email: data.result.data.email,
              position: {
                label: formatUserPosition(data.result.data.position),
                value: data.result.data.position,
              },
              role: { label: formatUserRole(data.result.data.role), value: data.result.data.role },
              password: '',
              confirmPassword: '',
            },
          });
        }
      } catch (e) {
        navigate(ROUTES.NOT_FOUND, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lazyGetUserQuery, navigate, userId]);

  useUpdateEffect(() => {
    passwordRef.current?.focus();
  }, [passwordVisible]);

  useUpdateEffect(() => {
    confirmPasswordRef.current?.focus();
  }, [confirmPasswordVisible]);

  const togglePasswordVisibility = useCallback(() => {
    setPasswordVisible(prevValue => !prevValue);
  }, []);

  const toggleConfirmPasswordVisibility = useCallback(() => {
    setConfirmPasswordVisible(prevValue => !prevValue);
  }, []);

  const handleCancelClick = useCallback(() => {
    navigate(ADMIN_ROUTES.USERS);
  }, [navigate]);

  const userPositionOptions = useMemo<AutocompleteOption[]>(() => {
    return (
      getUserPositionsQuery.data?.result.data.map(userPosition => ({
        label: formatUserPosition(userPosition),
        value: userPosition,
      })) || []
    );
  }, [getUserPositionsQuery.data]);

  const userRoleOptions = useMemo<AutocompleteOption[]>(() => {
    return (
      getUserRolesQuery.data?.result.data.map(userRole => ({
        label: formatUserRole(userRole),
        value: userRole,
      })) || []
    );
  }, [getUserRolesQuery.data]);

  if (isLoadingLazyGetUserQuery) {
    return (
      <div className={styles.loading}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <form className={styles.container__form} onSubmit={formik.handleSubmit}>
        <Typography color="text.primary" mb="25px" variant="h5">
          {userId ? 'Edit' : 'Create'} User
        </Typography>
        <TextField
          error={formik.touched.firstName && !!formik.errors.firstName}
          helperText={formik.touched.firstName ? formik.errors.firstName : undefined}
          label="First Name*"
          name="firstName"
          sx={FIELD_SX}
          value={formik.values.firstName}
          variant="outlined"
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
        />
        <TextField
          error={formik.touched.lastName && !!formik.errors.lastName}
          helperText={formik.touched.lastName ? formik.errors.lastName : undefined}
          label="Last Name*"
          name="lastName"
          sx={FIELD_SX}
          value={formik.values.lastName}
          variant="outlined"
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
        />
        <TextField
          error={formik.touched.email && !!formik.errors.email}
          helperText={formik.touched.email ? formik.errors.email : undefined}
          label="Email*"
          name="email"
          sx={FIELD_SX}
          type="email"
          value={formik.values.email}
          variant="outlined"
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
        />
        <Autocomplete
          error={formik.touched.position && !!formik.errors.position}
          helperText={formik.touched.position ? formik.errors.position : undefined}
          options={userPositionOptions}
          placeholder="Position*"
          sx={FIELD_SX}
          value={formik.values.position}
          onBlur={() => formik.setFieldTouched('position')}
          onChange={value => formik.setFieldValue('position', value)}
        />
        <Autocomplete
          error={formik.touched.role && !!formik.errors.role}
          helperText={formik.touched.role ? formik.errors.role : undefined}
          options={userRoleOptions}
          placeholder="Role*"
          sx={FIELD_SX}
          value={formik.values.role}
          onBlur={() => formik.setFieldTouched('role')}
          onChange={value => formik.setFieldValue('role', value)}
        />
        <TextField
          error={formik.touched.password && !!formik.errors.password}
          helperText={formik.touched.password ? formik.errors.password : undefined}
          inputRef={passwordRef}
          label="Password*"
          name="password"
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={togglePasswordVisibility}>
                    {passwordVisible ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
          sx={FIELD_SX}
          type={passwordVisible ? 'text' : 'password'}
          value={formik.values.password}
          variant="outlined"
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
        />
        <TextField
          error={formik.touched.confirmPassword && !!formik.errors.confirmPassword}
          helperText={formik.touched.confirmPassword ? formik.errors.confirmPassword : undefined}
          inputRef={confirmPasswordRef}
          label="Confirm Password*"
          name="confirmPassword"
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={toggleConfirmPasswordVisibility}>
                    {confirmPasswordVisible ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
          sx={FIELD_SX}
          type={confirmPasswordVisible ? 'text' : 'password'}
          value={formik.values.confirmPassword}
          variant="outlined"
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
        />
        <div className={styles.container__buttons}>
          <Button color="secondary" onClick={handleCancelClick}>
            Cancel
          </Button>
          <Button loading={isLoadingCreateUserMutation || isLoadingEditUserMutation} type="submit">
            {userId ? 'Save' : 'Submit'}
          </Button>
        </div>
      </form>
    </div>
  );
};
